import * as React from 'react';

import { Skeleton } from 'antd';
import { Column, Row, Space, Text } from 'farmhub-ui-core';
import moment from 'moment';

import { useAppSelector } from 'app/hooks';
import { NameText } from 'components/Text';
import { env } from 'env';
import { selectAppState, selectProductUnit } from 'slice';
import { formatAppearPhoneNumber, getFullAddress } from 'utils';

import Paragraph from '../components/Paragraph';
import GroupProductTable from './components/GroupProductTable';
import {
  CQRCode,
  WrapperContent,
  WrapperNote,
  WrapperSignature,
} from './styles';

interface WarehouseBillContentProps {
  order: any;
  isLoading: boolean;
  onCreateImage?: () => void;
  onChangeOrderInfo?: (val: any) => void;
  padding?: string;
}

const WarehouseBillContent = ({
  order,
  isLoading,
  onCreateImage,
  onChangeOrderInfo,
  padding,
}: WarehouseBillContentProps): JSX.Element => {
  const app = useAppSelector(selectAppState);
  const [orderInfo, setOrderInfo] = React.useState<any>({});

  const formatOrder = React.useMemo(() => {
    if (order?.orderItems?.length && isLoading === false) {
      const formatOrderItems = order.orderItems.map(item => {
        return {
          ...item,
          customerLossQuantity: parseFloat(
            (
              (item.supplierDeliveredQuantity || 0) -
              (item.customerReceivedQuantity || 0)
            ).toFixed(4)
          ),
        };
      });

      order.entitySubCategories.forEach(item => {
        item.orderItems.forEach(orderItem => {
          orderItem.customerLossQuantity = parseFloat(
            (
              (orderItem.supplierDeliveredQuantity || 0) -
              (orderItem.customerReceivedQuantity || 0)
            ).toFixed(4)
          );
        });
      });

      order.orderItems = formatOrderItems;
    }

    return order;
  }, [order, isLoading]);

  const handleUpdateData = (val: any) => {
    if (typeof val === 'object' && val !== null) {
      onChangeOrderInfo && onChangeOrderInfo(val);
      setOrderInfo((prevState: any) => {
        return {
          ...prevState,
          ...val,
        };
      });
      onCreateImage && onCreateImage();
    }
  };

  return (
    <WrapperContent className="bill-content" padding={padding}>
      <Row justifyContent="space-between" styles={{ gap: 12 }} width="100%">
        <Column styles={{ gap: 2 }} width="100%">
          {isLoading ? (
            <>
              <Row justifyContent="center">
                <div>
                  <Skeleton.Input
                    size="small"
                    style={{ width: '120px', alignItems: 'center' }}
                  />
                  <Space height={3} />
                  <Skeleton.Input
                    size="small"
                    style={{ width: '120px', alignItems: 'center' }}
                  />
                  <Space height={3} />
                  <Skeleton.Input
                    size="small"
                    style={{ width: '120px', alignItems: 'center' }}
                  />
                </div>
              </Row>
              <Space height={6} />
            </>
          ) : (
            <>
              <Row justifyContent="center" width="100%">
                <Text
                  color="black"
                  lineHeight="20px"
                  styles={{
                    flexShrink: 0,
                    fontSize: '18px',
                    fontWeight: '700 !important',
                  }}
                  weight="bold"
                >
                  PHIẾU SƠ CHẾ
                </Text>
              </Row>
              <Space height={3} />
              <Row justifyContent="center" width="100%">
                <div>
                  <Row
                    alignItems="flex-start"
                    justifyContent="center"
                    width="100%"
                  >
                    <Paragraph
                      style={{
                        width: 'fit-content',
                      }}
                      weight="extraBold"
                      onSubmit={(e: any) => {
                        handleUpdateData({ time: e });
                      }}
                    >
                      {orderInfo?.time ??
                        `Ngày: ${moment().format('DD/MM/YYYY')}`}
                    </Paragraph>
                  </Row>
                  <Row
                    alignItems="flex-start"
                    justifyContent="center"
                    width="100%"
                  >
                    <Paragraph
                      weight="extraBold"
                      onSubmit={(e: any) => {
                        handleUpdateData({ supplierName: e });
                      }}
                    >
                      {orderInfo?.supplierName ??
                        `Nhà cung cấp: ${order.supplier?.displayName}`}
                    </Paragraph>
                  </Row>
                </div>
              </Row>
              <Space height={6} />
            </>
          )}
        </Column>
        {isLoading ? (
          <Skeleton.Input style={{ width: 64, height: 64 }} />
        ) : order?.code ? (
          <CQRCode
            level="L"
            // imageSettings={{ src: LOGO_QR_CODE, height: 28, width: 28 }}
            value={`${
              app?.domains?.hubBilling?.domain ?? env.service.hubbillingWebApp
            }/${order.code}`}
          />
        ) : (
          <></>
        )}
      </Row>
      <Space height={6} />
      <GroupProductTable isLoading={isLoading} order={formatOrder} />
      <WrapperNote id="signature" isLoading={isLoading}>
        <Row
          alignItems="flex-start"
          styles={{
            padding: '4px',
          }}
        >
          {isLoading ? (
            <Skeleton.Input
              size="small"
              style={{ width: '100px', alignItems: 'center' }}
            />
          ) : (
            <>
              <Row styles={{ 'flex-shrink': 0 }} width="fit-content">
                <Text color="black" lineHeight="22px">
                  Ghi chú của KH:
                </Text>
              </Row>
              <Space width={6} />
              <Paragraph
                weight="extraBold"
                onSubmit={(e: any) => {
                  handleUpdateData({ customerNote: e });
                }}
              >
                {orderInfo?.customerNote ?? (order?.customerNote || '')}
              </Paragraph>
            </>
          )}
        </Row>
      </WrapperNote>
      <WrapperNote id="signature" isLoading={isLoading}>
        <Row
          alignItems="flex-start"
          styles={{
            padding: '4px',
          }}
        >
          {isLoading ? (
            <Skeleton.Input
              size="small"
              style={{ width: '100px', alignItems: 'center' }}
            />
          ) : (
            <>
              <Row styles={{ 'flex-shrink': 0 }} width="fit-content">
                <Text color="black" lineHeight="22px">
                  Ghi chú của NCC:
                </Text>
              </Row>
              <Space width={6} />
              <Paragraph
                weight="extraBold"
                onSubmit={(e: any) => {
                  handleUpdateData({ supplierNote: e });
                }}
              >
                {orderInfo?.supplierNote ?? (order?.supplierNote || '')}
              </Paragraph>
            </>
          )}
        </Row>
      </WrapperNote>
      <WrapperSignature id="signature" isLoading={isLoading}>
        <Row
          alignItems="flex-start"
          styles={{
            'border-right': `1px solid ${isLoading ? '#f0f0f0' : 'black'}`,
            padding: '4px',
          }}
          width="50%"
        >
          <Column>
            {isLoading ? (
              <Skeleton.Input
                size="small"
                style={{ width: '200px', alignItems: 'center' }}
              />
            ) : (
              <>
                <Text color="black">Người giao hàng:</Text>
                <Paragraph
                  weight="extraBold"
                  onSubmit={(e: any) => {
                    handleUpdateData({ deliveryInfo: e });
                  }}
                >
                  {orderInfo?.deliveryInfo ?? ''}
                </Paragraph>
              </>
            )}
          </Column>
        </Row>
        <Row alignItems="flex-start" styles={{ padding: '4px' }} width="50%">
          <Column>
            {isLoading ? (
              <>
                <Skeleton.Input
                  size="small"
                  style={{ width: '200px', alignItems: 'center' }}
                />
                <Space height={3} />
                <Skeleton.Input
                  size="small"
                  style={{ width: '200px', alignItems: 'center' }}
                />
              </>
            ) : (
              <>
                <Text color="black">Người nhận hàng: </Text>
                <Paragraph
                  weight="extraBold"
                  onSubmit={(e: any) => {
                    handleUpdateData({ receivedInfo: e });
                  }}
                >
                  {orderInfo?.receivedInfo ??
                    `${
                      order.additionalInfo?.address?.title
                    } ${formatAppearPhoneNumber(
                      order.additionalInfo?.address?.phoneNumber
                    )}, ${getFullAddress(
                      order.additionalInfo?.address?.address,
                      order.additionalInfo?.address?.district?.fullName,
                      order.additionalInfo?.address?.province?.fullName,
                      order.additionalInfo?.address?.ward?.fullName
                    )}`}
                </Paragraph>
              </>
            )}
          </Column>
        </Row>
      </WrapperSignature>
    </WrapperContent>
  );
};

export default WarehouseBillContent;
